import '@mantine/core/styles.css';
import '@/styles/global.scss';
import './fontawesome';

import { MantineProvider } from '@mantine/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { completeLogin, LoginPortalLink, startLogin } from '@/api/login';
import LoginPage from '@/pages/LoginPage/LoginPage';
import { extractLoginErrorMessage, isNotActivatedLoginError, REQUIRED_LOGIN_FIELDS_ERROR } from '@/utils/loginErrors';
import { getContextPath } from '@/utils/contextPath';
import { isValidEmail } from '@/utils/emailValidation';
import { resolveLoginRedirectTarget } from '@/utils/loginRedirect';
import { resolveLoginWidthMode } from '@/utils/loginWidthMode';
import { persistRememberedLoginState, readRememberedLoginState } from '@/utils/rememberedLogin';
import { redirectToNotActivatedPage } from '@/utils/authNavigation';
import { useLoginRecaptchaState } from '@/utils/loginRecaptchaState';
import { myllsTheme } from '@/styles/mantine-theme';
import { LuckyOrangeTracker } from '@/components/analytics/LuckyOrangeTracker';

const rootElement = document.getElementById('root');
const contextPath = getContextPath();

if (rootElement) {
  const root = createRoot(rootElement);

  const App = () => {
    const loginRedirectTarget = resolveLoginRedirectTarget(contextPath);
    const loginFormWidthMode = useMemo(() => resolveLoginWidthMode(window.location.search), []);
    const rememberedLoginState = useMemo(() => readRememberedLoginState(false), []);
    const [loginStep, setLoginStep] = useState<'email' | 'password'>('email');
    const [loginEmail, setLoginEmail] = useState(rememberedLoginState.email);
    const [loginPassword, setLoginPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(rememberedLoginState.rememberMe);
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [loginPortalLinks, setLoginPortalLinks] = useState<LoginPortalLink[]>([]);
    const {
      loginRecaptchaRequired,
      loginRecaptchaSiteKey,
      applyLoginRecaptchaState,
      bootstrapLoginRecaptcha,
      refreshLoginRecaptchaAfterFailedAuth
    } = useLoginRecaptchaState();

    useEffect(() => {
      void bootstrapLoginRecaptcha();
    }, [bootstrapLoginRecaptcha]);

    const handleLoginStart = useCallback(
      async (recaptchaToken?: string) => {
        const normalizedEmail = loginEmail.trim();
        if (!normalizedEmail) {
          setLoginError(REQUIRED_LOGIN_FIELDS_ERROR);
          return;
        }
        if (!isValidEmail(normalizedEmail)) {
          setLoginError('Please enter a valid email address.');
          return;
        }
        if (loginRecaptchaRequired && !recaptchaToken) {
          setLoginError('Please complete the reCAPTCHA challenge.');
          return;
        }
        setLoginLoading(true);
        setLoginError(null);
        try {
          const response = await startLogin(normalizedEmail, loginRedirectTarget, rememberMe, recaptchaToken);
          if (response.ssoRedirectUrl) {
            persistRememberedLoginState(response.email?.trim() || normalizedEmail, rememberMe);
            window.location.href = response.ssoRedirectUrl;
            return;
          }
          setLoginPortalLinks(response.portalLinks ?? []);
          applyLoginRecaptchaState(response);
          setLoginStep('password');
        } catch (error) {
          await refreshLoginRecaptchaAfterFailedAuth(error);
          setLoginError(extractLoginErrorMessage(error));
        } finally {
          setLoginLoading(false);
        }
      },
      [applyLoginRecaptchaState, loginEmail, loginRecaptchaRequired, rememberMe, loginRedirectTarget, refreshLoginRecaptchaAfterFailedAuth]
    );

    const handleLoginComplete = useCallback(
      async (recaptchaToken?: string) => {
        const normalizedEmail = loginEmail.trim();
        if (!normalizedEmail) {
          setLoginError(REQUIRED_LOGIN_FIELDS_ERROR);
          return;
        }
        if (!isValidEmail(normalizedEmail)) {
          setLoginError('Please enter a valid email address.');
          return;
        }
        if (!loginPassword) {
          setLoginError(REQUIRED_LOGIN_FIELDS_ERROR);
          return;
        }
        if (loginRecaptchaRequired && !recaptchaToken) {
          setLoginError('Please complete the reCAPTCHA challenge.');
          return;
        }
        setLoginLoading(true);
        setLoginError(null);
        try {
          await completeLogin({
            email: normalizedEmail,
            password: loginPassword,
            rememberMe,
            recaptchaToken
          });
          persistRememberedLoginState(normalizedEmail, rememberMe);
          window.location.href = loginRedirectTarget;
        } catch (error) {
          await refreshLoginRecaptchaAfterFailedAuth(error);
          if (isNotActivatedLoginError(error)) {
            redirectToNotActivatedPage(contextPath);
            return;
          }
          setLoginError(extractLoginErrorMessage(error));
        } finally {
          setLoginLoading(false);
        }
      },
      [contextPath, loginEmail, loginPassword, loginRecaptchaRequired, rememberMe, loginRedirectTarget, refreshLoginRecaptchaAfterFailedAuth]
    );

    const handleBack = useCallback(() => {
      setLoginStep('email');
      setLoginError(null);
    }, []);

    const handleRememberChange = useCallback((nextRememberMe: boolean) => {
      setRememberMe(nextRememberMe);
      if (nextRememberMe) {
        persistRememberedLoginState(loginEmail, true);
      } else {
        persistRememberedLoginState('', false);
      }
    }, [loginEmail]);

    return (
      <MantineProvider theme={myllsTheme} defaultColorScheme="light">
        <LuckyOrangeTracker pageName={loginStep === 'password' ? 'Login - Password' : 'Login'} />
        <LoginPage
          contextPath={contextPath}
          step={loginStep}
          formWidthMode={loginFormWidthMode}
          email={loginEmail}
          password={loginPassword}
          rememberMe={rememberMe}
          loading={loginLoading}
          error={loginError}
          recaptchaRequired={loginRecaptchaRequired}
          recaptchaSiteKey={loginRecaptchaSiteKey}
          portalLinks={loginPortalLinks}
          onEmailChange={setLoginEmail}
          onPasswordChange={setLoginPassword}
          onRememberChange={handleRememberChange}
          onStart={handleLoginStart}
          onComplete={handleLoginComplete}
          onBack={handleBack}
        />
      </MantineProvider>
    );
  };

  root.render(<App />);
}
